import React, { Fragment } from 'react';
import { localize } from 'react-localize-redux';
import styled from 'styled-components';
import BaseTable, { Column, AutoResizer, SortOrder } from 'react-base-table';
import moment from 'moment';
import 'react-base-table/styles.css';

import { groupListByKey } from 'helpers/tableHelpers';
import { sortAlphaNum } from 'helpers/utilities';
import groupSvg from 'assets/group-2.svg';
import loadingGif from 'assets/yuhu-loading.gif';

import debounce from 'lodash/debounce';

const WAIT_TIME_IN_MS = 400;

const StyledTableContainer = styled.div`
  width: 100%;
  height: ${(props) => props.height};
  position: relative;
  .BaseTable {
    box-shadow: none;
  }

  .BaseTable .BaseTable__header-cell {
    font-weight: bold;
    height: 50px;
  }

  .BaseTable__header-cell-text {
    height: 20px;
    &:hover {
      overflow: visible;
      text-overflow: initial;
    }
  }

  .BaseTable .tableheader {
    font-size: 16px;
    font-weight: bold;
    color: var(--slate-grey);
    padding-left: 15px;
  }

  .BaseTable .tablerow {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .BaseTable__table-main {
    outline: none;
  }

  .BaseTable__header-row {
    background-color: #fff;
    color: var(--table-title-text);
    border-bottom: solid 1px var(--pale-grey);
  }

  .BaseTable__header-cell--sortable:hover {
    overflow: visible !important;
  }
  .BaseTable__overlay {
    position: sticky;
  }
`;

const StyledImgContainer = styled.div`
  display: ${(props) => (props.shouldRender ? 'flex' : 'none')};
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  flex-direction: column;
  align-items: center;
`;

const StyledLoader = styled.img`
  width: 200px;
`;

const StyledRow = styled.div`
  display: flex;
  &.tablerow {
    cursor: pointer;
  }
`;

const StyledColumn = styled.div`
  width: ${(props) => props.flexwidth}px;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 7.5px;
  max-height: 38px;
  &:first-child {
    padding-left: 15px;
  }
`;

const StyledIllustration = styled.img`
  width: 80%;
  max-width: 576px;
  margin-bottom: 24px;
`;

const StyledTextNoMatch = styled.p`
  width: 100%;
  max-width: 269px;
  font-family: 'Circular-Bold', 'Circluar', sans-serif;
  font-size: 16px;
  text-align: center;
`;

// We are de-bouncing the click event to avoid multiple clicks making individual API calls(requests) to our database.
// With this de-bounce we only allow the last click event(trailing) within 400 ms to execute.
const debouncedHandleClick = debounce(
  (onRowClick, rowData) => {
    if (onRowClick) {
      onRowClick(rowData);
    }
  },
  WAIT_TIME_IN_MS,
  { leading: false, trailing: true }
);

export let DesktopTable = ({
  data,
  loadMoreData,
  columns,
  groupRowsBy,
  onRowClick,
  isLoading = false,
  height = '100vh',
  setRef,
  translate,
  onColumnClick,
  emptyTableText,
}) => {
  const [stickyIndex, setStickyIndex] = React.useState(0);
  const [sortBy, setSortBy] = React.useState({
    key: 'id',
    order: SortOrder.ASC,
  });

  const [groupedSortedList, headerIndices] = groupListByKey(data, groupRowsBy);
  let frozenData = [];

  if (groupRowsBy) {
    frozenData = groupedSortedList.slice(stickyIndex, stickyIndex + 1);
  }
  const handleScroll = React.useCallback(
    ({ scrollTop }) => {
      let stickyHeaderIndex = 0;
      headerIndices.forEach((headerIndex) => {
        if (headerIndex < Math.floor(scrollTop / 50)) {
          stickyHeaderIndex = headerIndex;
        }
      });
      setStickyIndex(stickyHeaderIndex);
    },
    [headerIndices, setStickyIndex]
  );

  const handleOnEndReached = React.useCallback(
    ({ distanceFromEnd }) => {
      if (distanceFromEnd <= 20) loadMoreData();
    },
    [loadMoreData]
  );

  const columnRenderer = ({ data, dataKey, rowData }) => {
    return columnRenderFunctionsMap[dataKey]({ data, dataKey, rowData });
  };

  const columnRenderFunctionsMap = columns.reduce((prev, curr) => {
    if (curr.render) {
      prev[curr.dataKey] = curr.render;
    } else {
      prev[curr.dataKey] = ({ data }) => data;
    }
    return prev;
  }, {});

  const onColumnSort = (sortBy) => {
    if (onColumnClick) {
      onColumnClick(sortBy);
    }
    setSortBy(sortBy);
  };

  const sortedTableData = sortTableData({
    tableData: groupedSortedList,
    headerIndices,
    key: sortBy.key,
    order: sortBy.order,
    onColumnClick: onColumnClick,
  });

  const rowRenderer = ({ rowData, columns, ...rest }) => {
    const handleRowClick = () => debouncedHandleClick(onRowClick, rowData);

    if (rowData.tableHeader) {
      return (
        <StyledRow
          key={`tableHeader-${rowData.tableHeader}-rowKey`}
          {...rest}
          className="tableheader"
          onClick={handleRowClick}
        >
          {rowData.tableHeader}
        </StyledRow>
      );
    } else {
      return (
        <StyledRow key={rowData.key || rowData.id} className="tablerow" onClick={handleRowClick}>
          {columns.map((column) => (
            <StyledColumn
              key={`${rowData.id}-${column.dataKey}`}
              flexwidth={column.width}
              style={{ whiteSpace: column.dataKey === 'status' ? 'nowrap' : 'initial' }}
            >
              {columnRenderer({
                dataKey: column.dataKey,
                data: rowData[column.dataKey],
                rowData: rowData,
              })}
            </StyledColumn>
          ))}
        </StyledRow>
      );
    }
  };

  const tableData = sortedTableData.slice(1).map((data) => {
    return {
      ...data,
      key: `${data.id}-${data.property_id}-${data.lease_id}`,
      id: `${data.id}-${data.property_id}-${data.lease_id}`,
      original_id: data.id,
    };
  });

  return (
    <StyledTableContainer height={height}>
      <AutoResizer>
        {({ width, height }) => (
          <BaseTable
            data={tableData}
            width={width}
            height={height}
            frozenData={frozenData}
            onScroll={handleScroll}
            rowRenderer={rowRenderer}
            sortBy={sortBy}
            onColumnSort={onColumnSort}
            ref={setRef}
            onEndReached={handleOnEndReached}
            onEndReachedThreshold={20}
          >
            {columns.map((column, i) => (
              <Column
                title={column.title}
                key={column.dataKey}
                dataKey={column.dataKey}
                sortable={column.sortable}
                width={column.width}
              />
            ))}
          </BaseTable>
        )}
      </AutoResizer>
      <StyledImgContainer shouldRender={isLoading}>
        <StyledLoader src={loadingGif} alt="loader" />
      </StyledImgContainer>
      <StyledImgContainer shouldRender={!isLoading && sortedTableData.length === 0}>
        {emptyTableText ? (
          <StyledTextNoMatch>{emptyTableText}</StyledTextNoMatch>
        ) : (
          <Fragment>
            <StyledIllustration src={groupSvg} alt="no results found" />
            <StyledTextNoMatch>
              {translate('landlordPortal.desktopTable.hmmNoMatchToSearch')}
            </StyledTextNoMatch>
          </Fragment>
        )}
      </StyledImgContainer>
    </StyledTableContainer>
  );
};
DesktopTable = localize()(DesktopTable);

const sortTableData = ({ tableData, headerIndices, key, order, onColumnClick }) => {
  const tableDataGroups = [];
  for (let i = 0; i < headerIndices.length; i++) {
    tableDataGroups.push(tableData.slice(headerIndices[i] + 1, headerIndices[i + 1]));
  }
  let sortedTableData = [];
  tableDataGroups.forEach((tableDataGroup, i) => {
    sortedTableData = sortedTableData.concat(
      [tableData[headerIndices[i]]],
      (onColumnClick && tableDataGroup) ||
        tableDataGroup.sort((a, b) => {
          if (
            [
              'last_active',
              'scheduled_starts_at',
              'request_date',
              'last_updated',
              'post_date',
              'expiration_date',
              'book_date',
              'move_in_date',
              'last_reminded',
              'post_date',
              'expiration_date',
              'insurance_expiry_date',
              'last_performed_at',
            ].includes(key)
          ) {
            return order === SortOrder.ASC
              ? sortByTime(a[key], b[key])
              : sortByTime(b[key], a[key]);
          } else {
            return order === SortOrder.ASC
              ? sortAlphaNum(a[key], b[key])
              : sortAlphaNum(b[key], a[key]);
          }
        })
    );
  });
  return sortedTableData;
};

function sortByTime(a, b) {
  if (!moment(b).isValid()) return -1;
  return moment.utc(a).diff(moment.utc(b));
}
